import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ThemeToggle from '../../../components/ThemeToggle';
import { useDarkMode } from '../../../hooks/useDarkMode';

const navLinks = [
  { href: '#como-funciona', label: 'Como funciona' },
  { href: '#estilos', label: 'Estilos' },
  { href: '#depoimentos', label: 'Depoimentos' },
  { href: '#preco', label: 'Preço' },
  { href: '#faq', label: 'FAQ' },
];

export default function Header() {
  const [scrollY, setScrollY] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isDark = useDarkMode();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const progress = Math.min(scrollY / 200, 1);
  const bgColor = progress > 0.05
    ? `rgba(255,255,255,${isDark ? 0 : 0.70 * progress})`
    : 'transparent';
  const backdropBlur = progress > 0.05 ? `blur(${12 * progress}px)` : 'none';
  const shadow = progress > 0.1 ? `0 1px 24px rgba(0,0,0,${0.06 * progress})` : 'none';

  const handleNav = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      className="w-full z-50 transition-colors duration-200"
      style={{
        background: bgColor,
        backdropFilter: backdropBlur,
        WebkitBackdropFilter: backdropBlur,
        boxShadow: shadow,
        borderBottom: progress > 0.1 ? `1px solid var(--border-soft)` : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-24 flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center cursor-pointer" style={{ overflow: 'visible' }}>
          <img
            src={isDark ? '/logo-dark.png' : '/logo-light.png'}
            alt="Inspira Músicas"
            className="w-auto object-contain"
            style={{ height: 52 }}
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-12">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className="text-lg font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
              style={{ color: 'var(--earth-strong)', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0em' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--gold)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--earth-strong)')}
            >
              {link.label}
            </button>
          ))}
          <Link
            to="/blog"
            className="text-lg font-medium transition-colors duration-200 whitespace-nowrap"
            style={{ color: 'var(--earth-strong)', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0em', textDecoration: 'none' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--gold)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--earth-strong)')}
          >
            Blog
          </Link>
        </nav>

        {/* CTA + Theme */}
        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <button
            onClick={() => handleNav('#preco')}
            className="px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
            style={{
              background: 'var(--gold)',
              color: '#fff',
              fontFamily: "'DM Sans', sans-serif",
              boxShadow: '0 2px 12px rgba(200,131,42,0.25)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--gold-hover)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(200,131,42,0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--gold)';
              e.currentTarget.style.boxShadow = '0 2px 12px rgba(200,131,42,0.25)';
            }}
          >
            Pedir Minha Música
          </button>
        </div>

        {/* Mobile: theme toggle + burger */}
        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle />
          <button
            className="w-9 h-9 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{ color: 'var(--earth-strong)' }}
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="md:hidden px-4 pb-5 pt-2 flex flex-col gap-3"
          style={{
            background: bgColor,
            borderTop: '1px solid rgba(58,38,24,0.08)',
          }}
        >
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className="text-sm font-medium py-2 text-left cursor-pointer"
              style={{ color: 'var(--earth-strong)', fontFamily: "'DM Sans', sans-serif" }}
            >
              {link.label}
            </button>
          ))}
          <Link
            to="/blog"
            onClick={() => setMobileOpen(false)}
            className="text-sm font-medium py-2 text-left"
            style={{ color: 'var(--earth-strong)', fontFamily: "'DM Sans', sans-serif", textDecoration: 'none' }}
          >
            Blog
          </Link>
          <button
            onClick={() => handleNav('#preco')}
            className="mt-2 px-5 py-2.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap text-center"
            style={{ background: 'var(--gold)', color: '#fff', fontFamily: "'DM Sans', sans-serif" }}
          >
            Pedir Minha Música
          </button>
        </div>
      )}
    </header>
  );
}
